
import { v4 as uuidv4 } from 'uuid';
import { Medication, MedicationForm, DosagePattern } from '@/types/medication';
import { MEDICATION_TIMING, DEFAULT_MEDICATION_TIMING } from '@/constants/medicationConstants';

interface RawMedicationData {
  id?: string;
  name?: string;
  medicationName?: string;
  dosage?: string;
  form?: MedicationForm;
  instructions?: string;
  timing?: string;
  time?: string;
  cycle?: string;
  frequency?: string;
  withFood?: boolean | string;
  foodInstructions?: string;
  category?: string;
  isPRN?: boolean;
  duration?: string;
  startDate?: string;
  endDate?: string;
  taken?: boolean;
  strictTiming?: boolean; // Add strictTiming property
}

export class MedicationTransformService {
  /**
   * Standardizes raw medication data into a consistent Medication object
   * @param rawData - Raw data from various sources that may include medication information
   * @returns Standardized Medication object with consistent properties and types
   */
  static standardizeMedicationData(rawData: any): Medication {
    // First standardize the foodInstructions to determine if it affects withFood
    const standardizedFoodInstructions = this.standardizeFoodInstructions(rawData.foodInstructions);
    
    // If foodInstructions is 'with', it should override withFood to be true
    // Otherwise, use the provided withFood value or default
    const withFoodValue = standardizedFoodInstructions === 'with' ? 
      true : this.standardizeWithFood(rawData.withFood);
    
    const standardized: Medication = {
      id: rawData.id || uuidv4(),
      name: rawData.medicationName || rawData.name || '',
      form: rawData.form || MedicationForm.Tablet,
      dosage: rawData.dosage || '',
      instructions: rawData.instructions || `Take ${rawData.dosage || ''} ${rawData.cycle || 'daily'}`,
      category: this.standardizeCategory(rawData.category),
      timing: this.standardizeTiming(rawData.timing || rawData.time, rawData.times),
      time: this.standardizeTiming(rawData.timing || rawData.time, rawData.times) as 'morning' | 'afternoon' | 'evening' | 'bedtime',
      cycle: rawData.cycle || 'daily',
      // Preserve cycle-related properties
      cycleLength: rawData.cycleLength,
      activeLength: rawData.activeLength,
      inactiveLength: rawData.inactiveLength,
      cycleStart: rawData.cycleStart,
      frequency: this.standardizeFrequency(rawData.frequency),
      withFood: withFoodValue, // Use the value determined above
      foodInstructions: standardizedFoodInstructions,
      duration: rawData.duration || '',
      startDate: rawData.startDate,
      endDate: rawData.endDate,
      taken: rawData.taken || false,
      isPRN: rawData.isPRN || false,
      strictTiming: rawData.strictTiming ?? false, // Plus tier feature
      lastUpdated: new Date().toISOString()
    };

    return this.validateMedication(standardized);
  }

  private static standardizeCategory(category?: string): 'prescription' | 'otc' | 'supplement' {
    if (!category) return 'prescription';
    const normalized = category.toLowerCase();
    if (normalized.includes('otc')) return 'otc';
    if (normalized.includes('supplement')) return 'supplement';
    return 'prescription';
  }

  /**
   * Standardizes medication timing information based on string input or time preferences object
   * 
   * @param timing - Optional string indicating the medication timing
   * @param timePreferences - Optional object containing boolean flags for different times of day
   * @returns A standardized timing value from the medication constants
   */
  private static standardizeTiming(timing?: string, timePreferences?: {morning?: boolean, afternoon?: boolean, evening?: boolean, bedtime?: boolean}): 'morning' | 'afternoon' | 'evening' | 'bedtime' {
    // If we have timePreferences with time selections, prioritize them
    if (timePreferences && typeof timePreferences === 'object') {
      // Safely filter entries that have truthy values
      const selectedTimes = Object.entries(timePreferences || {}).filter(([_, value]) => Boolean(value));
      
      // Keep exact user selection if there's only one time period selected
      if (selectedTimes.length === 1 && selectedTimes[0] && Array.isArray(selectedTimes[0])) {
        const selectedTime = selectedTimes[0][0]; // Get the key (time period name)
        // Map directly to constants to ensure consistency
        if (selectedTime === 'morning') return MEDICATION_TIMING.MORNING as 'morning';
        if (selectedTime === 'afternoon') return MEDICATION_TIMING.AFTERNOON as 'afternoon';
        if (selectedTime === 'evening') return MEDICATION_TIMING.EVENING as 'evening';
        if (selectedTime === 'bedtime' || selectedTime === 'night') return MEDICATION_TIMING.BEDTIME as 'bedtime';
      }
      
      // When multiple time periods are selected, prioritize in this order:
      // bedtime > evening > afternoon > morning
      if (selectedTimes.length > 1 && timePreferences) {
        // Priority order implementation with null checking
        if (timePreferences.bedtime === true) return MEDICATION_TIMING.BEDTIME as 'bedtime';
        if (timePreferences.evening === true) return MEDICATION_TIMING.EVENING as 'evening';
        if (timePreferences.afternoon === true) return MEDICATION_TIMING.AFTERNOON as 'afternoon';
        if (timePreferences.morning === true) return MEDICATION_TIMING.MORNING as 'morning';
      }
    }
    
    // Fall back to string parsing if no structured preferences
    if (!timing) {
      console.log(`[TIMING DEBUG] No timing provided, defaulting to morning`);
      return DEFAULT_MEDICATION_TIMING as 'morning';
    }
    
    // Ensure we're working with a string
    const normalized = (timing || '').toString().toLowerCase();
    console.log(`[TIMING DEBUG] Normalized timing string:`, normalized);
    
    // Handle both 'night' and 'bedtime' values consistently
    if (normalized === 'night' || normalized === 'bedtime') {
      console.log(`[TIMING DEBUG] Direct match for night/bedtime -> BEDTIME`);
      return MEDICATION_TIMING.BEDTIME as 'bedtime';
    }
    
    // Properly handle bedtime/night timing with more comprehensive checks
    if (normalized.includes('night') || 
        normalized.includes('bed') || 
        normalized === 'pm' || 
        normalized === 'p.m.') {
      console.log(`[TIMING DEBUG] Partial match for night/bedtime patterns -> BEDTIME`);
      return MEDICATION_TIMING.BEDTIME as 'bedtime';
    }
    
    if (normalized.includes('evening')) {
      console.log(`[TIMING DEBUG] Matched evening pattern -> EVENING`);
      return MEDICATION_TIMING.EVENING as 'evening';
    }
    if (normalized.includes('afternoon')) {
      console.log(`[TIMING DEBUG] Matched afternoon pattern -> AFTERNOON`);
      return MEDICATION_TIMING.AFTERNOON as 'afternoon';
    }
    if (normalized.includes('morning')) {
      console.log(`[TIMING DEBUG] Matched morning pattern -> MORNING`);
      return MEDICATION_TIMING.MORNING as 'morning';
    }
    
    console.log(`[TIMING DEBUG] No pattern matches, defaulting to morning`);
    return DEFAULT_MEDICATION_TIMING as 'morning';
  }

  private static standardizeFrequency(frequency?: string | { pattern: string; weekDay?: string }): Medication['frequency'] {
    if (!frequency) {
      return { pattern: 'daily' as DosagePattern };
    }

    if (typeof frequency === 'object' && frequency.pattern === 'weekly') {
      // Convert weekDay to daysOfWeek array for compatibility
      const daysOfWeek = frequency.weekDay ? [frequency.weekDay] : ['Monday'];
      return { 
        pattern: 'weekly' as DosagePattern,
        daysOfWeek 
      };
    }

    const normalized = typeof frequency === 'string' ? frequency.toLowerCase() : '';
    if (normalized.includes('twice')) {
      return { pattern: 'twice-daily' as DosagePattern };
    }
    if (normalized.match(/every\s*(\d+)\s*hours?/)) {
      const hours = parseInt(normalized.match(/every\s*(\d+)\s*hours?/)?.[1] || '24');
      return {
        pattern: 'every-x-hours' as DosagePattern,
        customInterval: hours
      };
    }
    return { pattern: 'daily' as DosagePattern };
  }

  private static standardizeWithFood(withFood?: boolean | string): boolean {
    if (typeof withFood === 'boolean') return withFood;
    if (!withFood) return false;
    return ['yes', 'true', 'with', 'food'].includes(withFood.toLowerCase());
  }

  private static standardizeFoodInstructions(instructions?: string): 'before' | 'with' | 'after' | undefined {
    if (!instructions) return undefined;
    const normalized = instructions.toLowerCase();
    if (normalized.includes('before')) return 'before';
    if (normalized.includes('after')) return 'after';
    if (normalized.includes('with')) return 'with';
    return undefined;
  }

  private static validateMedication(medication: Medication): Medication {
    if (!medication.name) throw new Error('Medication name is required');
    if (!medication.dosage) throw new Error('Medication dosage is required');
    if (!medication.form) throw new Error('Medication form is required');
    return medication;
  }
}
